import { Component, HostListener, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Event, NavigationStart, Router } from '@angular/router';
import { EventEmitter } from '@angular/core';
import { Subject } from 'rxjs';
import { AccountService } from '../../services/account/account.services';
import { ProductsService } from '../../services/product/product.services';
import { CategoriesService } from '../../services/categories/categories.service';
import { CategoryItem } from '../../models/category-item.model';
import { ShoppingCartService } from '../../services/cart/shopping-cart.service';
import BigNumber from 'bignumber.js';
import { FeaturesStatusService } from '../../services/util/features-status.service';
import { FeatureNames } from '../../models/feature-names.enum';
import { PlatformBalance } from '../../models/wallet.balance';
import { TranslateService } from '@ngx-translate/core';
import { SessionStorageService } from 'angular-web-storage';
import { LocalStorageKeys } from '../../utils/local.storage.keys';
import { LanguagesEnum } from '../../models/languages.enum';
import { SnackbarComponent } from '../../snackbar/snackbar.component';
import { TranslationConstants } from '../../services/util/translation.constants';
import { SnackBarTheme } from '../../models/snackbar-theme.enum';
import { AllCategories } from '../../models/all-category.enum';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {

    @Output() public returnLoggedUser: EventEmitter<any> = new EventEmitter();
    public loading: boolean = false;
    public loggedUser: any = { firstName: '', favorites: [] };
    public shoppingCart: any = { products: [] };
    public onDestroy: any = new Subject<void>();
    public favorites: any;
    public carts: number = 0;
    public urls: Array<string> = [];
    public menuBarItems: Array<CategoryItem> = [];
    public lineBarItems: Array<CategoryItem> = [];
    public searchTime: any;
    public searchValue: string = '';
    public isTokenEnabled: boolean = false;
    public isLetterOfCreditEnabled: boolean = false;
    public browserWidth: number = 0;
    public balances: PlatformBalance[] = [];
    public selectedLanguage: LanguagesEnum;
    public SEARCH_TIME_VALUE: number = 1500; // In milliseconds
    public apiOfflineBalance: boolean = false;

    constructor(
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly accountService: AccountService,
        private readonly productService: ProductsService,
        private readonly categoriesService: CategoriesService,
        private readonly shoppingService: ShoppingCartService,
        private readonly featuresStatusService: FeaturesStatusService,
        private readonly translate: TranslateService,
        private readonly sessionStorage: SessionStorageService,
        private readonly snackBarComponent: SnackbarComponent,
        private readonly translationConstants: TranslationConstants
    ) {
        this.router.events.subscribe((event: Event) => {
            if (event instanceof NavigationStart) {
                this.urls.push(event.url);

                if (!this.loggedUser
                    && this.urls[this.urls.length - 2]
                    && this.urls[this.urls.length - 2].match('login')) {
                    this.userLogged();
                }
            }
        });
    }

    public ngOnInit(): void {
        this.browserWidth = window.innerWidth;
        this.userLogged();
        this.loadCategories();
        this.featuresStatusService.getFeatureStatus(FeatureNames.TOKEN_HANDLING).subscribe(data => {
            this.isTokenEnabled = data;
        });
        this.featuresStatusService.getFeatureStatus(FeatureNames.LETTER_OF_CREDIT).subscribe(data => {
            this.isLetterOfCreditEnabled = data;
        });

        this.getLastResearched();

        this.selectedLanguage = this.accountService.getUserLanguage();
        this.setLanguage();
    }

    public loadBalance(): void{
        this.shoppingService.allBalances().subscribe(balanceList => {
            this.balances = this.sortBalancesByName(balanceList?.filter(balance => balance?.balance > 0));
        }, error => {
            this.apiOfflineBalance = true;
            this.snackBarComponent.openSnackBar(this.translationConstants.translate(`snackbar.systemUnavailable`), SnackBarTheme.error, 3000);
        });
    }

    public sortBalancesByName(list: Array<PlatformBalance>): Array<PlatformBalance> {
        const sortedList: Array<PlatformBalance> = list?.sort((a, b) => {
            return a?.unitOfMoney < b?.unitOfMoney ? -1 : a?.unitOfMoney > b?.unitOfMoney ? 1 : 0;
        });
        return sortedList || [];
    }


    /**
     * @param event to check the width of the browser.
     */
    @HostListener('window:resize', ['$event'])
    public onResize(event) {
        this.browserWidth = window.innerWidth;
    }

    /**
     * Checks the browser width to hide the menu item 'Favorite'.
     */
    public checkWidth(): boolean {
        if (this.browserWidth < 428) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * To keep the last research in the input
     */
    public getLastResearched(): void {
        this.route.params.subscribe(params => {
            if (params['search']) {
                this.searchValue = params['search'].replace(/\&/g, ' ');
            }
        });
    }

    public userLogged(): void {
        this.accountService.isAuthenticated(true).subscribe(success => {
            if (success) {
                this.accountService.getUserDetails().subscribe((user: any) => {
                    if (user?.firstName) {
                        this.loggedUser = user;
                        this.loggedUser.firstName = `${this.loggedUser.firstName.charAt(0).toUpperCase() + this.loggedUser.firstName.slice(1, this.loggedUser.firstName.length)}`
                    }
                    this.returnLoggedUser.emit(this.loggedUser);
                });

                this.loadBalance();

                this.productService.countFavorites().subscribe();
            }

            this.shoppingService.getShoppingCarts(false).subscribe(success => {
                if (success?.length > 0) {
                    for (let cart of success) {
                        for (let item of cart.items) {
                            if(!new BigNumber(item.quantity).isNaN()) {
                                this.carts += new BigNumber(item.quantity).toNumber();
                            }
                        }
                    }
                }
            }, error => {

            });

            this.productService.favoriteCounted$.subscribe(data => {
                this.favorites = data;
            });
        }).add(() => {
        });
    }


    public loadCategories(): void {
        this.categoriesService.getCategories().subscribe(categories => {
            this.menuBarItems = categories?.filter(cat => !cat.parent_category_id);
            this.menuBarItems?.forEach((cat) => {
                cat.subcategories = [];
                categories?.forEach((subCat) => {

                    if (cat.id === subCat.parent_category_id) {
                        cat.subcategories.push(subCat);
                    }
                });
            });

            this.lineBarItems = this.menuBarItems?.slice(0, 6);
        });
    }

    public searchProduct(forceSearch: boolean = false): void {
        // Clear the timeout to wait a little bit the user to finish typing the search
        clearTimeout(this.searchTime);
        this.searchTime = setTimeout(() => {
            const newValue = this.searchValue.replace(/\s/g, '&');
            this.router.navigate([`/search/${newValue}`]);
        }, forceSearch ? 0 : this.SEARCH_TIME_VALUE);
    }

    public clearSearchTimeout(): void {
        clearTimeout(this.searchTime);
    }

    public goHome(): void {
        this.router.navigate(['/home']);
    }

    public goLogin(): void {
        this.router.navigate(['/account/login']);
    }

    public goOrder(): void {
        this.accountService.isAuthenticated(true).subscribe(success => {
            if (success) {
                this.router.navigate(['/order-history']);
            } else {
                this.router.navigate(['/account/login']);
            }
        });
    }

    public goToFavorites(): void {
        this.router.navigate(['/wishlist']);
    }

    public goToCart(): void {
        this.router.navigate(['/shopping-cart']);
    }

    public goToSignup(): void {
        this.router.navigate(['/account/signup']);
    }

    public searchCategory(category: CategoryItem, subCategory: boolean = false): void {
        const allSubcategories = this.translationConstants.translate(AllCategories.ALL);

        if (subCategory && !category.name.includes(allSubcategories)) {
            this.router.navigate([`search-category/${category?.name}`], { queryParams: { subcategoryParent: category?.id, parentCategoryId: category?.parent_category_id } });
        } else {
            this.router.navigate([`search-category/${category?.name}`], { queryParams: { categoryId: category?.id } });
        }
    }

    public async logout(): Promise<void> {
        this.loading = true;
        this.accountService.destroySession().subscribe(() => {
            window.location.reload();
        }).add(() => {
            this.loggedUser = undefined;
            this.loading = false;
        });
    }

    public async changePassword(): Promise<void> {
        this.router.navigate(['/account/forgot'], { queryParams: { 'isReset': true } });
    }

    public ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.complete();
    }

    public setLanguage(): void {
        this.sessionStorage.set(LocalStorageKeys.LANGUAGE_LIST, this.selectedLanguage);
        this.translate.use(this.selectedLanguage);
    }
}